import { styled } from 'opensolar-ui'
import { useTranslate } from 'ra-core'
import { FC, useEffect, useMemo, useState } from 'react'
interface PropTypes {
  endDateTime: string
  ui3styling?: boolean
}
const CountdownWrapper = styled('div', { name: 'CountdownWrapper' })(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignContent: 'center',
  gap: 50,
  maxWidth: '100%',
  [theme.breakpoints.down('sm')]: {
    marginBottom: 20,
    justifyContent: 'flex-start',
    gap: 15,
  },
  [theme.breakpoints.down('md')]: {
    gap: 15,
  },
}))
const Section = styled('div', { name: 'Section' })<{
  ui3styling?: boolean
}>(({ theme, ui3styling }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  textAlign: 'center',
  textTransform: 'uppercase',
  flex: 1,
  minWidth: 0,
  '& p': {
    fontSize: ui3styling ? 64 : 60,
    margin: 0,
    marginBottom: ui3styling ? 15 : 25,
    fontWeight: ui3styling ? 450 : 'normal',
    lineHeight: 1,
    ...(ui3styling && {
      background: 'linear-gradient(180deg, #1751D0 0%, #3C91E6 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      color: 'transparent',
    }),
  },
  '& span': {
    fontSize: ui3styling ? 16 : 12,
    fontWeight: ui3styling ? 400 : 500,
    letterSpacing: '0.5px',
    ...(ui3styling && {
      color: '#4C4B53',
      lineHeight: '24px',
    }),
  },
  [theme.breakpoints.down('md')]: {
    '& p': {
      fontSize: ui3styling ? '2rem' : 36,
      marginBottom: ui3styling ? 10 : 15,
    },
    '& span': {
      fontSize: ui3styling ? '0.875rem' : 11,
      ...(ui3styling && {
        lineHeight: '130%',
      }),
    },
  },
  [theme.breakpoints.down('sm')]: {
    '& p': {
      fontSize: ui3styling ? '1.5rem' : 28,
      marginBottom: ui3styling ? 8 : 12,
    },
    '& span': {
      fontSize: ui3styling ? '0.75rem' : 10,
    },
  },
}))
const Countdown: FC<PropTypes> = ({ endDateTime, ui3styling }) => {
  const translate = useTranslate()
  const [timeNow, setTimeNow] = useState(new Date().getTime())
  const { days, hours, minutes, seconds } = useMemo(() => {
    const countDownDate = new Date(endDateTime).getTime()
    let timespan = countDownDate - timeNow
    let retValues = {
      days: Math.floor(timespan / (1000 * 60 * 60 * 24)),
      hours: Math.floor((timespan % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutes: Math.floor((timespan % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((timespan % (1000 * 60)) / 1000),
    }
    Object.keys(retValues).forEach((x) => {
      if (retValues[x] <= 0) retValues[x] = 0
    })
    return retValues
  }, [endDateTime, timeNow])
  useEffect(() => {
    const interval = setInterval(() => setTimeNow(new Date().getTime()), 1000)
    return () => {
      clearInterval(interval)
    }
  }, [])
  const pad = (num: number) => String(num).padStart(2, '0')
  if (!endDateTime) return null
  if (days === 0 && hours === 0 && minutes === 0 && seconds === 0) return null
  return (
    <CountdownWrapper>
      {days !== 0 && (
        <Section ui3styling={ui3styling}>
          <p>{pad(days)}</p>
          <span>{translate('days')}</span>
        </Section>
      )}
      <Section ui3styling={ui3styling}>
        <p>{pad(hours)}</p>
        <span>{translate('hours')}</span>
      </Section>
      <Section ui3styling={ui3styling}>
        <p>{pad(minutes)}</p>
        <span>{translate('minutes')}</span>
      </Section>
      <Section ui3styling={ui3styling}>
        <p>{pad(seconds)}</p>
        <span>{translate('seconds')}</span>
      </Section>
    </CountdownWrapper>
  )
}
export default Countdown
